import * as React from 'react';
import { IFullTipSchema } from 'shared/repositories/text-search/entities/tips/FullTipSchema';

import { TipsContext } from './context';
import { fetchMainTips } from 'shared/services/fetchMainTips';
import { fetchTips } from 'shared/services/fetchTips';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { useSelector } from 'react-redux';
import { selectQueryString } from 'shared/selectors/queryString';
import { fetchTipsV4 } from 'shared/services/fetchTipsV4';
import { IFullTipV2Schema } from 'shared/repositories/text-search/entities/tips/FullTipV2Schema';

type TTipsProviderProps = {
  children: React.ReactNode;
};

export const TipsProvider: React.FC<TTipsProviderProps> = ({ children }) => {
  const context = useApplicationContext();
  const query = useSelector(selectQueryString);

  const [mainTips, setMainTips] = React.useState<IFullTipSchema[]>([]);
  const [searchTips, setSearchTips] = React.useState<IFullTipSchema[]>([]);
  const [mobileTips, setMobileTips] = React.useState<IFullTipV2Schema[]>([]);
  const [clickedTip, setClickedTip] = React.useState<IFullTipSchema | null>(null);
  const [isTipsDisabled, setIsTipsDisabled] = React.useState<boolean>(false);

  const loadMainTips = React.useCallback(
    async (searchQuery?: string) => {
      try {
        setIsTipsDisabled(true);
        const response = await fetchMainTips(context, searchQuery || query);
        setMainTips(response?.tips || []);
      } catch (error) {
        console.error('Error fetching main tips:', error);
        setMainTips([]);
      } finally {
        setIsTipsDisabled(false);
      }
    },
    [context, query],
  );

  const loadSearchTips = React.useCallback(
    async (searchQuery: string) => {
      try {
        setIsTipsDisabled(true);
        const response = await fetchTips(context, searchQuery);
        setSearchTips(response?.tips || []);
      } catch (error) {
        console.error('Error fetching search tips:', error);
        setSearchTips([]);
      } finally {
        setIsTipsDisabled(false);
      }
    },
    [context],
  );

  const loadMobileTips = React.useCallback(
    async (searchQuery: string) => {
      try {
        setIsTipsDisabled(true);
        const response = await fetchTipsV4(context, searchQuery);
        setMobileTips(response?.tips || []);
      } catch (error) {
        console.error('Error fetching mobile tips:', error);
        setMobileTips([]);
      } finally {
        setIsTipsDisabled(false);
      }
    },
    [context],
  );

  const handleMainTipClick = React.useCallback(
    (tip: IFullTipSchema) => {
      setIsTipsDisabled(true);
      setClickedTip({ ...tip });
      loadMainTips(query);
    },
    [loadMainTips, query],
  );

  // Загрузка главных типсов при монтировании
  React.useEffect(() => {
    loadMainTips();
  }, [loadMainTips]);

  const contextValue = React.useMemo(
    () => ({
      mainTips,
      searchTips,
      mobileTips,
      clickedTip,
      isTipsDisabled,
      loadMainTips,
      loadSearchTips,
      loadMobileTips,
      handleMainTipClick,
    }),
    [
      mainTips,
      searchTips,
      mobileTips,
      clickedTip,
      isTipsDisabled,
      loadMainTips,
      loadSearchTips,
      loadMobileTips,
      handleMainTipClick,
    ],
  );

  return <TipsContext.Provider value={contextValue}>{children}</TipsContext.Provider>;
};
